import React, { useEffect, useRef } from 'react';
import './Article.scss';
import { gsap } from './gsap';
import { createPopper } from '@popperjs/core';
import Hero from './Components/Hero/Hero';

import Header from './Components/common-header/Header';
import Footer from './Components/common-footer/Footer';
import InlinePhoto from './Components/Photos/InlinePhoto';
import FullWidthPhoto from './Components/Photos/FullWidthPhoto';
import Chapter1 from './Components/Chapters/Chapter1';
import Chapter2 from './Components/Chapters/Chapter2';
import Chapter3 from './Components/Chapters/Chapter3';
import Chapter4 from './Components/Chapters/Chapter4';
import Chapter5 from './Components/Chapters/Chapter5';
import Chapter6 from './Components/Chapters/Chapter6';
import Chapter7 from './Components/Chapters/Chapter7';
import PullQuoteWImg from './Components/PullQuote/PullQuoteWImg';
import InlineVideo from './Components/Photos/InlineVideo';
import FullWidthVideo from './Components/Photos/FullWidthVideo';
import contents from './contents';
import RelatedContents from './Components/RelatedContents/RelatedContents';
import Reference from './Components/Reference/Reference';
import CtaButton from './Components/CtaButton/CtaButton';
import MobileCarousel from './Components/Photos/MobileCarousel';

//for fullWidth Photos
import fullbleedPhoto3 from './Assets/fullBleedPhoto3.jpg';
import fullBleedPhoto3M from './Assets/fullBleedPhoto3M.jpg';

//pullQuotePhotos
import pqImg1 from './Assets/pqImg1.jpg';
import pqImg2 from './Assets/pqImg2.jpg';

function Article({ heroBackgroundType, inlinePhotoSize, logoPlacement }) {
  //below states are for the audio player placement. See README for detail.

  //create refs for photos here if you want to add scale animation on scroll
  const inline1 = useRef();
  const inline2 = useRef();
  const inline3 = useRef();
  const inline4 = useRef();
  const inline5 = useRef();
  const inline6 = useRef();
  const inline7 = useRef();
  const allPhotosRef = useRef();
  allPhotosRef.current = [inline1, inline2, inline3, inline4, inline5, inline6, inline7]; // put all photo refs in this array

  const screenSizeRef = useRef(); //

  const photoSize = inlinePhotoSize !== undefined ? inlinePhotoSize : contents.inlinePhotos.size;

  //creates/shows tooltip element when mouse enters
  const handleMouseEnterTooltip = (e, currentRef) => {
    const content = currentRef.current;
    createPopper(e.target, content, {
      placement: 'bottom',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 5]
          }
        }
      ]
    });
    content.style.visibility = 'visible';
  };

  //hides tooltip element when mouse leaves
  const handleMouseLeaveTooltip = (currentRef) => {
    currentRef.current.style.visibility = 'hidden';
  };

  useEffect(() => {
    allPhotosRef.current.forEach((photo) => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: photo.current,
          start: '20% bottom',
          end: 'top center',
          scrub: 1
        }
      });
      tl.to(photo.current, {
        width: '100%',
        height: '100%',
        duration: 3
      });
    });
  }, []);

  return (
    <div className="article">
      <div className="height" ref={screenSizeRef} />
      <Header clientName={contents.header} />
      <Hero
        handleMouseEnterTooltip={handleMouseEnterTooltip}
        handleMouseLeaveTooltip={handleMouseLeaveTooltip}
        heroBackgroundType={heroBackgroundType}
        logoPlacement={logoPlacement}
      />
      <Chapter1 />
      <PullQuoteWImg
        quote={'Intel called us. And they said, ‘We’ve got to do something here.’'}
        author={'David Hartshorn,'}
        descrip={'CEO, Geeks Without Frontiers'}
        photo={pqImg1}
      />
      <Chapter2 />
      <FullWidthPhoto
        photo={fullbleedPhoto3}
        photoM={fullBleedPhoto3M}
        alt={'containers'}
        caption={
          ' (Left) Ukrainian children create art on laptops inside the PCC at RomExpo In Bucharest, Romania as part of World Vision’s Happy Bubbles program. (Right) Joe Simmons, Geeks Without Frontiers and Mykolay Latushkin, Help Ukrainians, unpack a 50-inch monitor to support community center broadcasts and education. (Bogdan Dinca for CORE/Geeks Without Frontiers/World Vision) '
        }
      />
      <Chapter3 />
      <PullQuoteWImg
        quote={
          'One company can’t solve this problem. Money can’t solve this problem. We said, ‘Hey, let’s open source this and see if people want to partner with us to solve the problem of digital adoption.’'
        }
        author={'Daniel Gutwein,'}
        descrip={'Senior Director, Education and N50 Project, Intel'}
        photo={pqImg2}
      />
      <Chapter4 />

      <InlineVideo
        vidLink={'https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/n50Globe.mp4'}
        vidLinkM={
          'https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/n50GlobeM.mp4'
        }
        caption={
          'Since its founding, The N50 Project has grown to include approximately 100 member companies.'
        }
      />
      <Chapter5 />
      <MobileCarousel />
      <Chapter6 />
      <FullWidthVideo
        vidLink={'https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/map.mp4'}
        vidLinkM={'https://courageous-cdn-media-bucket.s3.amazonaws.com/intelWonderful/mapM.mp4'}
        caption={
          'To date, seven PCCs have been delivered, with four additional units scheduled for deployment within Ukraine.'
        }
      />
      <Chapter7 />
      <InlinePhoto
        photoSize={photoSize}
        photoContents={contents.inlinePhotos.contents[3]}
        photoRef={inline4}
      />
      <InlinePhoto
        photoSize={photoSize}
        photoContents={contents.inlinePhotos.contents[4]}
        photoRef={inline5}
      />
      <InlinePhoto
        photoSize={photoSize}
        photoContents={contents.inlinePhotos.contents[5]}
        photoRef={inline6}
      />

      <InlinePhoto
        photoSize={photoSize}
        photoContents={contents.inlinePhotos.contents[2]}
        photoRef={inline3}
      />
      <InlinePhoto
        photoSize={photoSize}
        photoContents={contents.inlinePhotos.contents[6]}
        photoRef={inline7}
      />
      <CtaButton
        handleMouseEnterTooltip={handleMouseEnterTooltip}
        handleMouseLeaveTooltip={handleMouseLeaveTooltip}
      />
      {/* <div className="article__divider" /> */}
      <Reference />
      <RelatedContents />
      <Footer />
    </div>
  );
}

export default Article;
